/**
 * FieldTreeGroup Component
 *
 * Collapsible group header with field count badge.
 * Contains FieldTreeItem children.
 */

import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FieldTreeItem } from './FieldTreeItem';
import type { FieldTreeGroupProps } from './types';

export function FieldTreeGroup({
  group,
  fields,
  selectedField,
  onSelect,
  isSearching,
  fieldIndices,
  focusedIndex,
}: FieldTreeGroupProps) {
  // When searching, groups are always expanded
  const [isExpanded, setIsExpanded] = useState(!group.defaultCollapsed);
  const effectiveExpanded = isSearching || isExpanded;

  // Don't render empty groups
  if (fields.length === 0) {
    return null;
  }

  const handleToggle = () => {
    if (!isSearching) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className="py-1" role="group" aria-labelledby={`group-${group.key}`}>
      {/* Group Header */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={isSearching}
        id={`group-${group.key}`}
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 text-left',
          'text-xs font-semibold text-fg-muted uppercase tracking-wider',
          'hover:bg-bg-muted rounded transition-colors',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          isSearching && 'cursor-default hover:bg-transparent'
        )}
        aria-expanded={effectiveExpanded}
      >
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-150',
            effectiveExpanded && 'rotate-90',
            isSearching && 'opacity-50'
          )}
          aria-hidden="true"
        />
        {group.icon && <span className="flex-shrink-0">{group.icon}</span>}
        <span className="flex-1 truncate">{group.label}</span>
        <span
          className={cn(
            'flex-shrink-0 px-1.5 py-0.5 text-xs rounded-full',
            'bg-bg-muted text-fg-subtle'
          )}
        >
          {fields.length}
        </span>
      </button>

      {/* Group Content */}
      {effectiveExpanded && (
        <div
          className="pl-2 mt-0.5 space-y-0.5"
          role="listbox"
          aria-label={`${group.label} fields`}
        >
          {fields.map((field) => {
            const fieldIndex = fieldIndices.get(field.name);
            const isFocused = fieldIndex !== undefined && fieldIndex === focusedIndex;

            return (
              <FieldTreeItem
                key={field.name}
                field={field}
                isSelected={field.name === selectedField}
                isFocused={isFocused}
                onClick={() => onSelect(field.name)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FieldTreeGroup;
